/**
 * Angular route guard for controlling access to various application routes
 * based on authentication status, user permissions, and connectivity.
 */

// Angular
import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
} from '@angular/router';

// Local library
import { GUEST, Environment as env } from "@amwager/util-ts";

// Local file
import { PermissionService, PusherService, SessionService } from '../services';
import { CommonFacade } from '../+state';


/**
 * Interface defining the services required by the auth guard
 */
interface IService {
  commonFacade: CommonFacade,
  sessionService: SessionService,
  pusherService: PusherService,
  permissionService: PermissionService,
  router: Router,
  url: string
}

/**
 * Route guard — allows or denies navigation based on auth + permissions
 * @param {ActivatedRouteSnapshot} next Route being navigated to
 * @param {RouterStateSnapshot} state Router state
 * @returns {boolean} True when the route can be activated
 */
export const accessGuardRouteFn: CanActivateFn = (
  next: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const service: IService = {
    commonFacade: inject(CommonFacade),
    sessionService: inject(SessionService),
    router: inject(Router),
    url: state.url,
    pusherService: inject(PusherService),
    permissionService: inject(PermissionService)
  };

  return checkPageRoute(service);
}

/**
 * Decide whether the current route is accessible for the user
 * @param {IService} service Injected service bag
 * @returns {boolean} True when the route can be activated
 */
const checkPageRoute = (service: IService): boolean => {
  // if (!navigator.onLine) {
  //   if (service.url !== '/wager') {
  //     service.commonFacade.setIsConnectedInternet('checkPageRoute', false);
  //     return false;
  //   }
  // }

  // check pages to redirect if cookie has token
  if (
    service.url === '/login'
    || service.url === '/signup'
    || service.url === '/forgot-password'
    || service.url === '/success-request-reset-password'
  ) {
    // skip pages and redirect to wager if has token
    // TODO: guest user
    if (service.sessionService.hasToken() && (service.sessionService.getUserEmail() !== GUEST)) {
      redirectToHomeScreen(service);
      return false;
    }
  }

  if (service.url === '/complete-registration' || service.url === '/documents-verification') {
    if (service.sessionService.hasToken()) {
      if (service.permissionService.isGranted(
        service.sessionService.getRoleId(),
        service.url
      )) {
        service.commonFacade.setIsShowNavigation(false);
        return true;
      }
      else {
        service.router.navigateByUrl('/wager');
        return false;
      }
    }
  }

  // if to show navigation or not
  if (
    service.url === '/login'
    || service.url === '/signup'
    || service.url === '/complete-registration'
    || service.url === '/documents-verification'
    || service.url === '/forgot-password'
    || service.url === '/success-request-reset-password'
    || service.url === '/update-password'
    || service.url === '/success-update-password'
  ) {
    service.commonFacade.setIsShowNavigation(false);
    return true;
  }

  // Results is restricted to real (non-guest) signed-in users.
  // Guests and signed-out visitors are sent to login.
  if (service.url === '/results' || service.url.startsWith('/results/')) {
    const isRealUser = service.sessionService.hasToken()
      // && (service.sessionService.getUserEmail() !== GUEST);

    if (!isRealUser && !env.Environment.enableComponentView) {
      service.router.navigate(['/login']);
      return false;
    }
  }

  // Replays is restricted to real (non-guest) signed-in users.
  // Guests and signed-out visitors are sent to login.
  if (service.url === '/replays' || service.url.startsWith('/replays/')) {
    const isRealUser = service.sessionService.hasToken()
      // && (service.sessionService.getUserEmail() !== GUEST);

    if (!isRealUser && !env.Environment.enableComponentView) {
      service.router.navigate(['/login']);
      return false;
    }
  }

  // Program products are bought against the account, so the page is
  // restricted to signed-in users. Everyone else is sent to login.
  if (service.url === '/program' || service.url.startsWith('/program/')) {
    if (!service.sessionService.hasToken() && !env.Environment.enableComponentView) {
      service.router.navigate(['/login']);
      return false;
    }
  }

  // Post-login surfaces that live inside the wager shell — same
  // header / footer chrome, no session-blocking rules. Kept as a
  // small allow-list so adding a new page (e.g. `/results`, `/replays`) only
  // needs one line here and one route in `wager-feature-mat`.
  const authenticatedShellRoutes = ['/wager', '/results', '/replays', '/program'];
  if (authenticatedShellRoutes.some((path) => (
    service.url === path || service.url.startsWith(`${path}/`)
  ))) {
    service.commonFacade.setIsShowNavigation(true);
    service.commonFacade.setIsShowFooter(true);
    return true;
  }

  return false;
}

/**
 * Redirect authenticated users away from auth pages
 * @param {IService} service Injected service bag
 * @param {string} homeScreen Target URL (defaults to '/wager')
 * @returns {void}
 */
const redirectToHomeScreen = (service: IService, homeScreen = '/wager'): void => {
  // if enable test component view for routes then skip process
  if (env.Environment.enableComponentView) return;

  service.router.navigateByUrl(homeScreen);
}
